import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Group,
  Paper,
  ScrollArea,
  SegmentedControl,
  Select,
  Stack,
  Text,
  Transition,
  Tooltip,
} from '@mantine/core';
import { useReducedMotion } from '@mantine/hooks';
import { IconArrowLeft, IconChevronRight } from '@tabler/icons-react';
import {
  RefObject,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import StepExecutionsRenderer from '@/pages/Run/components/StepExecutionsRenderer.tsx';
import { HistoricalScopePresentationProvider } from '@/pages/Run/components/HistoricalScopePresentationContext.ts';
import { AutoExpandPlan } from '@/types/autoExpandPlan.ts';
import { AutomationStep } from '@/types/automationSteps.ts';
import { StepType } from '@/types/automationFields.ts';
import { Run } from '@/types/runs.ts';
import {
  type ChildScopeChoice,
  ChildScopeContext,
  getChildScopeChoiceId,
  getChildScopeHeading,
  getChildScopeKey,
  getChildScopeParentSteps,
  getNearestChildScopeChoiceId,
} from '@/pages/Run/components/ScopeStack/childScopeModel.ts';
import { useChildScopeChoices } from '@/pages/Run/components/ScopeStack/useChildScopeChoices.ts';
import ScopeNavigatorViewport from '@/pages/Run/components/ScopeStack/ScopeNavigatorViewport.tsx';

const scopeColumnWidth = 480;
const scopeColumnGap = 16;
const minimumTwoColumnWidth = scopeColumnWidth * 2 + scopeColumnGap;

type ScopeSectionState = {
  key: string;
  context: ChildScopeContext;
  selectedChoiceId: string | null;
  isHistorical: boolean;
};

type RenderedScopeSection = ScopeSectionState & {
  isClosing?: boolean;
};

type Props = {
  run: Run;
  stepsById: Record<string, AutomationStep>;
  autoExpandPlan?: AutoExpandPlan | null;
  scrollableRef?: RefObject<HTMLDivElement | null>;
  scrollingHeader?: ReactNode;
};

const HorizontalRunScopeView = ({
  run,
  stepsById,
  autoExpandPlan,
  scrollableRef,
  scrollingHeader,
}: Props) => {
  const [sections, setSections] = useState<ScopeSectionState[]>([]);
  const [renderedSections, setRenderedSections] = useState<RenderedScopeSection[]>([]);
  const [isNarrow, setIsNarrow] = useState(false);
  const [scopeOffsets, setScopeOffsets] = useState<Record<string, number>>({});
  // An exit transition outlives the active section state, so retain its last measured position.
  const scopeOffsetsRef = useRef<Record<string, number>>({});
  const [trackMetrics, setTrackMetrics] = useState({ isOverflowing: false, scrollWidth: 0 });
  const trackRef = useRef<HTMLDivElement>(null);
  const scrollRailRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const scopeCanvasRef = useRef<HTMLDivElement>(null);
  const activeScopeRef = useRef<HTMLDivElement>(null);
  const reducedMotion = useReducedMotion();
  const activeScopeIdentity = sections.at(-1)
    ? `${sections.at(-1)!.key}:${sections.at(-1)!.context.stepRun.id}`
    : null;

  useEffect(() => {
    setRenderedSections((current) => {
      const next = sections.map((section) => ({ ...section, isClosing: false }));
      const closing = current
        .filter((section) => !sections.some((nextSection) => nextSection.key === section.key))
        .map((section) => ({ ...section, isClosing: true }));
      const merged = [...next, ...closing];

      if (
        merged.length === current.length &&
        merged.every((section, index) => {
          const previous = current[index];
          return (
            previous?.key === section.key &&
            previous.isClosing === section.isClosing &&
            previous.context === section.context &&
            previous.selectedChoiceId === section.selectedChoiceId &&
            previous.isHistorical === section.isHistorical
          );
        })
      ) {
        return current;
      }

      return merged;
    });
  }, [sections]);

  useEffect(() => {
    if (!containerRef.current || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      setIsNarrow((entry?.contentRect.width ?? 0) < minimumTwoColumnWidth);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const closeFrom = useCallback((index: number) => {
    setSections((current) => current.slice(0, index));
  }, []);

  const openChildScope = useCallback((context: ChildScopeContext, isHistorical = false) => {
    const parentDepth = context.parentSteps.length;
    const key = getChildScopeKey(context);
    setSections((current) => {
      const retained = current.filter(
        (section) => section.context.parentSteps.length < parentDepth,
      );
      const existing = current.find(
        (section) => section.context.parentSteps.length === parentDepth && section.key === key,
      );
      return [
        ...retained,
        existing
          ? { ...existing, context, isHistorical }
          : { key, context, selectedChoiceId: null, isHistorical },
      ];
    });
  }, []);

  const refreshChildScopeContext = useCallback((context: ChildScopeContext) => {
    const key = getChildScopeKey(context);
    setSections((current) => {
      let changed = false;
      const next = current.map((section) => {
        if (section.key !== key) return section;
        if (
          section.context.stepRun.id === context.stepRun.id &&
          section.context.stepExecution === context.stepExecution
        ) {
          return section;
        }
        changed = true;
        return { ...section, context };
      });
      return changed ? next : current;
    });
  }, []);

  const updateSelectedScope = useCallback(
    (sectionIndex: number, selectedChoiceId: string | null) => {
      setSections((current) =>
        current
          .slice(0, sectionIndex + 1)
          .map((section, index) =>
            index === sectionIndex ? { ...section, selectedChoiceId } : section,
          ),
      );
    },
    [],
  );

  useEffect(() => {
    if (!trackRef.current || sections.length === 0 || isNarrow) return;
    const frame = window.requestAnimationFrame(() => {
      const track = trackRef.current;
      if (!track || track.scrollWidth <= track.clientWidth) return;
      track.scrollTo({
        left: track.scrollWidth - track.clientWidth,
        behavior: reducedMotion ? 'auto' : 'smooth',
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [activeScopeIdentity, isNarrow, reducedMotion, sections.length]);

  useLayoutEffect(() => {
    if (isNarrow) {
      setTrackMetrics((current) =>
        current.isOverflowing || current.scrollWidth !== 0
          ? { isOverflowing: false, scrollWidth: 0 }
          : current,
      );
      return;
    }

    const track = trackRef.current;
    if (!track) return;
    const updateMetrics = () => {
      const scrollWidth = track.scrollWidth;
      const isOverflowing = scrollWidth > track.clientWidth + 1;
      setTrackMetrics((current) =>
        current.isOverflowing === isOverflowing && current.scrollWidth === scrollWidth
          ? current
          : { isOverflowing, scrollWidth },
      );
      if (scrollRailRef.current) {
        scrollRailRef.current.scrollLeft = track.scrollLeft;
      }
    };
    const syncRail = () => {
      if (scrollRailRef.current) {
        scrollRailRef.current.scrollLeft = track.scrollLeft;
      }
    };
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateMetrics);
    observer?.observe(track);
    if (scopeCanvasRef.current) observer?.observe(scopeCanvasRef.current);
    track.addEventListener('scroll', syncRail);
    updateMetrics();

    return () => {
      observer?.disconnect();
      track.removeEventListener('scroll', syncRail);
    };
  }, [isNarrow, sections.length]);

  useEffect(() => {
    if (trackMetrics.isOverflowing && scrollRailRef.current && trackRef.current) {
      scrollRailRef.current.scrollLeft = trackRef.current.scrollLeft;
    }
  }, [trackMetrics.isOverflowing]);

  useEffect(() => {
    if (sections.length === 0 || !isNarrow) return;
    const frame = window.requestAnimationFrame(() => {
      activeScopeRef.current?.scrollIntoView?.({
        behavior: reducedMotion ? 'auto' : 'smooth',
        block: 'start',
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [activeScopeIdentity, isNarrow, reducedMotion, sections.length]);

  const anchorStepRunIds = useMemo(
    () => sections.map((section) => section.context.stepRun.id),
    [sections],
  );

  useLayoutEffect(() => {
    if (isNarrow || anchorStepRunIds.length === 0) {
      setScopeOffsets((current) => {
        const keepClosingOffsets =
          !isNarrow &&
          renderedSections.some(
            (section) => section.isClosing || !sections.some((next) => next.key === section.key),
          );
        const next = keepClosingOffsets || Object.keys(current).length === 0 ? current : {};
        scopeOffsetsRef.current = next;
        return next;
      });
      return;
    }

    const canvas = scopeCanvasRef.current;
    if (!canvas) return;

    let frame: number | null = null;
    const measureOffsets = () => {
      frame = null;
      const canvasTop = canvas.getBoundingClientRect().top;
      const existingOffsets = scopeOffsetsRef.current;
      const nextOffsets = sections.reduce<Record<string, number>>((offsets, section) => {
        const anchor = getScopeOpenControl(section.context.stepRun.id);
        if (!anchor) return offsets;
        offsets[section.key] = Math.max(0, anchor.getBoundingClientRect().top - canvasTop);
        return offsets;
      }, {});
      renderedSections
        .filter((section) => section.isClosing)
        .forEach((section) => {
          if (existingOffsets[section.key] !== undefined) {
            nextOffsets[section.key] = existingOffsets[section.key];
          }
        });

      setScopeOffsets((current) => {
        const currentKeys = Object.keys(current);
        const nextKeys = Object.keys(nextOffsets);
        if (
          currentKeys.length === nextKeys.length &&
          nextKeys.every((key) => current[key] === nextOffsets[key])
        ) {
          scopeOffsetsRef.current = current;
          return current;
        }
        scopeOffsetsRef.current = nextOffsets;
        return nextOffsets;
      });
    };
    const scheduleMeasurement = () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(measureOffsets);
    };

    measureOffsets();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', scheduleMeasurement);
      return () => {
        window.removeEventListener('resize', scheduleMeasurement);
        if (frame !== null) window.cancelAnimationFrame(frame);
      };
    }

    const observer = new ResizeObserver(scheduleMeasurement);
    observer.observe(canvas);
    canvas.querySelectorAll<HTMLElement>('[data-scope-column]').forEach((column) => {
      observer.observe(column);
    });
    anchorStepRunIds.forEach((stepRunId) => {
      const anchor = getScopeOpenControl(stepRunId);
      if (anchor) observer.observe(anchor);
    });

    return () => {
      observer.disconnect();
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [anchorStepRunIds, isNarrow, renderedSections, scopeOffsets, sections]);

  const ancestorScopes = useMemo(
    () =>
      sections.slice(0, -1).map((section, index) => ({
        label: section.context.step.name,
        onClick: () => closeFrom(index + 1),
      })),
    [closeFrom, sections],
  );
  const columnWidth = isNarrow ? '100%' : scopeColumnWidth;
  const hasChildScopes = sections.length > 0;

  return (
    <ScopeNavigatorViewport
      containerRef={containerRef}
      verticalViewportRef={scrollableRef}
      scrollingHeader={scrollingHeader}
      chrome={
        <>
          {sections.length > 1 && (
            <Box w="100%" maw="100%" mx="auto">
              <Group component="nav" gap={4} mb={0} wrap="wrap" aria-label="Scope path">
                <Button
                  variant="subtle"
                  color="gray"
                  size="compact-xs"
                  px={2}
                  onClick={() => closeFrom(0)}
                >
                  Run results
                </Button>
                {ancestorScopes.map((scope, index) => (
                  <Group gap={4} key={`${scope.label}-${index}`} wrap="nowrap">
                    <IconChevronRight size={14} color="#868E96" />
                    <Button
                      variant="subtle"
                      color="gray"
                      size="compact-xs"
                      px={2}
                      onClick={scope.onClick}
                    >
                      {scope.label}
                    </Button>
                  </Group>
                ))}
              </Group>
            </Box>
          )}
          {!isNarrow && trackMetrics.isOverflowing && (
            <Box data-scope-scroll-rail pt={2} pb={4}>
              <ScrollArea
                type="always"
                scrollbars="x"
                scrollbarSize={8}
                h={12}
                aria-label="Horizontal scope navigation"
                viewportRef={scrollRailRef}
                onScrollPositionChange={({ x }) => {
                  const track = trackRef.current;
                  if (track && Math.abs(track.scrollLeft - x) > 0.5) {
                    track.scrollTo({ left: x });
                  }
                }}
              >
                <Box w={trackMetrics.scrollWidth} h={1} />
              </ScrollArea>
            </Box>
          )}
        </>
      }
    >
      <ScrollArea
        data-scope-horizontal-track
        type="never"
        scrollbars="x"
        viewportRef={trackRef}
        onScrollPositionChange={({ x }) => {
          if (scrollRailRef.current) scrollRailRef.current.scrollLeft = x;
        }}
        w="100%"
      >
        <Box
          ref={scopeCanvasRef}
          data-scope-canvas
          style={{
            minWidth: '100%',
            width: isNarrow ? '100%' : 'max-content',
            paddingBottom: 8,
          }}
        >
          <Group
            align="flex-start"
            gap={scopeColumnGap}
            wrap={isNarrow ? 'wrap' : 'nowrap'}
            justify={sections.length <= 1 || isNarrow ? 'center' : 'flex-start'}
          >
            <RootScopeColumn
              run={run}
              stepsById={stepsById}
              autoExpandPlan={autoExpandPlan}
              onOpenChildScope={openChildScope}
              onChildScopeContextChange={refreshChildScopeContext}
              width={columnWidth}
              isContext={hasChildScopes}
              activeScopeParentStepRunId={sections[0]?.context.stepRun.id}
            />
            {renderedSections.map((section) => {
              const sectionIndex = sections.findIndex((item) => item.key === section.key);
              const isClosing = section.isClosing || sectionIndex === -1;
              const isActive = !isClosing && sectionIndex === sections.length - 1;

              return (
                <Transition
                  key={section.key}
                  mounted={!isClosing}
                  transition={{
                    in: { opacity: 1, transform: 'translateX(0)' },
                    out: { opacity: 0, transform: 'translateX(12px)' },
                    transitionProperty: 'transform, opacity',
                  }}
                  duration={reducedMotion ? 0 : 180}
                  timingFunction="ease-out"
                  onExited={() => {
                    setRenderedSections((current) =>
                      current.filter((item) => item.key !== section.key || !item.isClosing),
                    );
                  }}
                >
                  {(transitionStyles) => (
                    <Box w={columnWidth} miw={0} style={transitionStyles}>
                      <ScopeColumn
                        run={run}
                        stepsById={stepsById}
                        section={section}
                        sectionIndex={Math.max(0, sectionIndex)}
                        autoExpandPlan={autoExpandPlan}
                        width={columnWidth}
                        onOpenChildScope={openChildScope}
                        onChildScopeContextChange={refreshChildScopeContext}
                        onSelect={updateSelectedScope}
                        onCollapse={() => closeFrom(Math.max(0, sectionIndex))}
                        backToLabel={
                          sectionIndex <= 0
                            ? 'run results'
                            : sections[sectionIndex - 1].context.step.name
                        }
                        isContext={!isClosing && sectionIndex < sections.length - 1}
                        activeScopeRef={isActive ? activeScopeRef : undefined}
                        verticalOffset={isNarrow ? 0 : (scopeOffsets[section.key] ?? 0)}
                        activeScopeParentStepRunId={
                          isClosing ? undefined : sections[sectionIndex + 1]?.context.stepRun.id
                        }
                        isExiting={isClosing}
                        reducedMotion={reducedMotion}
                      />
                    </Box>
                  )}
                </Transition>
              );
            })}
          </Group>
        </Box>
      </ScrollArea>
    </ScopeNavigatorViewport>
  );
};

type CommonColumnProps = {
  run: Run;
  stepsById: Record<string, AutomationStep>;
  autoExpandPlan?: AutoExpandPlan | null;
  width: number | string;
  onOpenChildScope: (context: ChildScopeContext, isHistorical?: boolean) => void;
  onChildScopeContextChange: (context: ChildScopeContext) => void;
  isContext?: boolean;
  activeScopeParentStepRunId?: string;
};

const RootScopeColumn = ({
  run,
  stepsById,
  autoExpandPlan,
  width,
  onOpenChildScope,
  onChildScopeContextChange,
  isContext = false,
  activeScopeParentStepRunId,
}: CommonColumnProps) => (
  <Box
    w={width}
    miw={0}
    aria-label={isContext ? 'Run results context' : 'Run results'}
    data-scope-state={isContext ? 'context' : 'active'}
    data-scope-column="root"
    style={isContext ? { opacity: 0.94 } : undefined}
  >
    <StepExecutionsRenderer
      run={run}
      enabled
      stepsById={stepsById}
      parentSteps={[]}
      autoExpandPlan={autoExpandPlan}
      childScopePresentation="horizontal"
      onOpenChildScope={onOpenChildScope}
      onChildScopeContextChange={onChildScopeContextChange}
      activeScopeParentStepRunId={activeScopeParentStepRunId}
    />
  </Box>
);

type ScopeColumnProps = CommonColumnProps & {
  section: ScopeSectionState;
  sectionIndex: number;
  onSelect: (index: number, selectedChoiceId: string | null) => void;
  onCollapse: () => void;
  backToLabel: string;
  activeScopeRef?: RefObject<HTMLDivElement | null>;
  verticalOffset: number;
  isExiting?: boolean;
  reducedMotion: boolean;
};

const ScopeColumn = ({
  run,
  stepsById,
  section,
  sectionIndex,
  autoExpandPlan,
  width,
  onOpenChildScope,
  onChildScopeContextChange,
  onSelect,
  onCollapse,
  backToLabel,
  isContext = false,
  activeScopeRef,
  verticalOffset,
  activeScopeParentStepRunId,
  isExiting = false,
  reducedMotion,
}: ScopeColumnProps) => {
  const appliedAutoExpandPlanRef = useRef<AutoExpandPlan | null>(null);
  const previousDefaultChoiceIdRef = useRef<string | null>(null);
  const context = section.context;
  const stepType = context.stepRun.step_type;
  const { items, defaultChoiceId, isRetryPresentationPending, isRetryPresentationError } =
    useChildScopeChoices({ context, stepsById, enabled: true });
  const targetScope = autoExpandPlan?.child_scopes_to_open.find(
    (scope) => scope.parent_step_run_id === context.stepRun.id,
  );
  const autoTargetItem = targetScope
    ? items.find(
        (item) =>
          item.childIndex === targetScope.child_index &&
          (targetScope.attempt_number == null || item.attemptNumber === targetScope.attempt_number),
      )
    : null;
  const autoChoiceId = autoTargetItem ? getChildScopeChoiceId(autoTargetItem) : null;
  const selectedChoiceId = section.selectedChoiceId ?? autoChoiceId ?? defaultChoiceId;
  const selectedItem =
    items.find((item) => getChildScopeChoiceId(item) === selectedChoiceId) ?? null;
  const heading = getChildScopeHeading(stepType, context.step.name);
  const showCompactSelector =
    items.length > 1 && items.length <= 2 && context.stepRun.step_type === StepType.FailureHandler;

  useEffect(() => {
    if (isExiting) return;
    const previousDefaultChoiceId = previousDefaultChoiceIdRef.current;
    previousDefaultChoiceIdRef.current = defaultChoiceId;
    if (
      stepType === StepType.Retry &&
      section.selectedChoiceId === null &&
      previousDefaultChoiceId !== null &&
      defaultChoiceId !== null &&
      previousDefaultChoiceId !== defaultChoiceId
    ) {
      onSelect(sectionIndex, null);
    }
  }, [defaultChoiceId, isExiting, onSelect, section.selectedChoiceId, sectionIndex, stepType]);

  useEffect(() => {
    if (isExiting) return;
    if (
      !autoExpandPlan ||
      !targetScope ||
      !autoChoiceId ||
      appliedAutoExpandPlanRef.current === autoExpandPlan
    ) {
      return;
    }
    onSelect(sectionIndex, autoChoiceId);
    appliedAutoExpandPlanRef.current = autoExpandPlan;
  }, [autoChoiceId, autoExpandPlan, isExiting, onSelect, sectionIndex, targetScope]);

  useEffect(() => {
    if (isExiting) return;
    if (items.length === 0) {
      if (stepType !== StepType.Retry) onCollapse();
      return;
    }
    if (!selectedItem) {
      onSelect(
        sectionIndex,
        getNearestChildScopeChoiceId(items, section.selectedChoiceId ?? defaultChoiceId),
      );
    }
  }, [
    defaultChoiceId,
    isExiting,
    items,
    onCollapse,
    onSelect,
    section.selectedChoiceId,
    sectionIndex,
    selectedItem,
    stepType,
  ]);

  return (
    <Paper
      ref={activeScopeRef}
      radius="md"
      p="sm"
      w={width}
      miw={0}
      withBorder
      shadow={isContext ? undefined : 'sm'}
      data-scope-state={isContext ? 'context' : 'active'}
      data-scope-column={section.key}
      data-scope-offset={verticalOffset}
      style={{
        ...(isContext ? { opacity: 0.94 } : {}),
        marginTop: verticalOffset,
        pointerEvents: isExiting ? 'none' : undefined,
        transition: reducedMotion ? undefined : 'margin-top 180ms ease-out',
      }}
    >
      <Stack gap={6}>
        <Group gap={6} wrap="nowrap">
          <Tooltip label={`Back to ${backToLabel}`}>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              onClick={onCollapse}
              aria-label={`Back to ${backToLabel}`}
              title={`Back to ${backToLabel}`}
            >
              <IconArrowLeft size={16} />
            </ActionIcon>
          </Tooltip>
          <Text size="sm" fw={600} style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
            {heading}
          </Text>
        </Group>
        {showCompactSelector && (
          <SegmentedControl
            size="xs"
            fullWidth
            value={selectedChoiceId ?? undefined}
            onChange={(value) => onSelect(sectionIndex, value)}
            data={items.map((item) => ({
              value: getChildScopeChoiceId(item),
              label: item.label,
            }))}
          />
        )}
        {items.length > 1 && !showCompactSelector && (
          <Select
            size="xs"
            searchable
            value={selectedChoiceId}
            onChange={(value) => onSelect(sectionIndex, value)}
            data={items.map((item) => ({
              value: getChildScopeChoiceId(item),
              label: item.outcomeLabel ? `${item.label} — ${item.outcomeLabel}` : item.label,
            }))}
            aria-label="Select child scope"
          />
        )}
        {stepType === StepType.Retry && selectedItem && <RetryAttemptContext item={selectedItem} />}
        {selectedItem ? (
          <Box mt="xs">
            <HistoricalScopePresentationProvider
              value={section.isHistorical || !!selectedItem.historical}
            >
              <StepExecutionsRenderer
                run={run}
                enabled
                stepsById={stepsById}
                parentSteps={getChildScopeParentSteps(context)}
                parentPath={selectedItem.parentPath ?? context.stepRun.full_path}
                childIndex={selectedItem.childIndex}
                attemptNumber={selectedItem.attemptNumber}
                scopeParentStepRunId={selectedItem.scopeParentStepRunId}
                autoExpandPlan={autoExpandPlan}
                childScopePresentation="horizontal"
                onOpenChildScope={(childContext) =>
                  onOpenChildScope(childContext, section.isHistorical || !!selectedItem.historical)
                }
                onChildScopeContextChange={onChildScopeContextChange}
                activeScopeParentStepRunId={activeScopeParentStepRunId}
              />
            </HistoricalScopePresentationProvider>
          </Box>
        ) : (
          <Text size="sm" c="dimmed">
            {stepType === StepType.Retry && isRetryPresentationPending
              ? 'Loading retry attempts...'
              : stepType === StepType.Retry && isRetryPresentationError
                ? 'Unable to load retry attempts.'
                : stepType === StepType.Retry
                  ? 'No retry attempts recorded.'
                  : 'No child results'}
          </Text>
        )}
      </Stack>
    </Paper>
  );
};

const getScopeOpenControl = (stepRunId: string) =>
  Array.from(document.querySelectorAll<HTMLElement>('[data-scope-open-control-for]')).find(
    (element) => element.dataset.scopeOpenControlFor === stepRunId,
  ) ?? document.getElementById(`step-run-${stepRunId}`);

const RetryAttemptContext = ({ item }: { item: ChildScopeChoice }) => (
  <Stack gap={3} aria-label="Attempt details">
    <Group gap={6}>
      <Badge size="xs" variant="outline" color="gray">
        {item.label}
      </Badge>
      {item.outcomeLabel && (
        <Badge size="xs" variant="light" color={getRetryOutcomeBadgeColor(item.outcomeTone)}>
          {item.outcomeLabel}
        </Badge>
      )}
    </Group>
    {item.detailText && (
      <Text size="xs" c="dimmed">
        {item.detailText}
      </Text>
    )}
    {item.lifecycleText && (
      <Text size="xs" c="dimmed">
        {item.lifecycleText}
      </Text>
    )}
  </Stack>
);

const getRetryOutcomeBadgeColor = (tone: ChildScopeChoice['outcomeTone']) => {
  switch (tone) {
    case 'green':
      return 'green';
    case 'red':
      return 'red';
    case 'yellow':
      return 'yellow';
    case 'blue':
      return 'blue';
    default:
      return 'gray';
  }
};

export default HorizontalRunScopeView;
